import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { AlertTriangle, Check, Key, Lock } from "lucide-react";

const resetPasswordSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Extract token from URL
  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get('token');
  const email = params.get('email');
  
  // Validate token
  useEffect(() => {
    if (!resetToken || !email) {
      setError("Invalid or missing reset information. Please request a new password reset.");
      setTokenValid(false);
      return;
    }
    
    const verifyToken = async () => {
      try {
        const response = await apiRequest("GET", `/api/auth/verify-reset-token?token=${resetToken}&email=${encodeURIComponent(email)}`);
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || "Invalid or expired reset token");
        }
        
        setTokenValid(true);
      } catch (err: any) {
        setError(err.message || "Failed to verify reset token. Please request a new password reset.");
        setTokenValid(false);
      }
    };
    
    verifyToken();
  }, [resetToken, email]);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: { password: string }) => {
      if (!resetToken || !email) {
        throw new Error("Missing reset information");
      }
      
      const response = await apiRequest("POST", "/api/auth/reset-password", {
        token: resetToken,
        email,
        password: data.password,
      });
      
      const jsonData = await response.json();
      
      if (!response.ok) {
        throw new Error(jsonData.message || "Failed to reset password");
      }
      
      return jsonData;
    },
    onSuccess: () => {
      setSuccess(true);
      form.reset();
      
      toast({
        title: "Password reset successful",
        description: "Your password has been updated. You can now log in with your new password.",
        variant: "default",
      });
      
      // Redirect to login after a short delay
      setTimeout(() => {
        setLocation("/login");
      }, 3000);
    },
    onError: (error: any) => {
      setError(error.message || "Failed to reset password. Please try again.");
    },
  });

  function onSubmit(data: ResetPasswordFormValues) {
    setError(null);
    
    // Remove confirmPassword before submitting
    const { confirmPassword, ...submitData } = data;
    resetPasswordMutation.mutate(submitData);
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-grow flex items-center justify-center bg-gray-50 py-12">
        <div className="w-full max-w-md px-4">
          <Card className="w-full shadow-lg border-0">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">Reset Your Password</CardTitle>
              <CardDescription className="text-center">
                Enter your new password below
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="mb-4 bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Your password has been reset successfully. Redirecting to login...
                  </AlertDescription>
                </Alert>
              )}
              
              {tokenValid === false && (
                <div className="text-center py-4">
                  <Key className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Invalid or Expired Link</h3>
                  <p className="text-gray-600 mb-4">
                    The password reset link is invalid or has expired. Please request a new one.
                  </p>
                  <Button 
                    onClick={() => setLocation('/forgot-password')}
                    variant="outline"
                    className="mx-auto"
                  >
                    Request New Reset Link
                  </Button>
                </div>
              )}
              
              {tokenValid === true && !success && (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      disabled={resetPasswordMutation.isPending}
                    >
                      {resetPasswordMutation.isPending ? "Resetting..." : "Reset Password"}
                    </Button>
                  </form>
                </Form>
              )}
              
              {tokenValid === null && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              )}
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4">
              <div className="text-sm text-center text-gray-500">
                Remembered your password?{" "}
                <Link href="/login" className="text-blue-600 hover:text-blue-800 font-medium">
                  Back to Login
                </Link>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}